import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import { supabase } from '@/lib/supabase';
import { setupLightRenovationSchedule, setupProjectSchedule, LIGHT_RENOVATION_SCHEDULE_TASKS } from '@/lib/projectScheduleSetup';
import { setupProjectChecklist } from '@/lib/projectChecklistSetup';
import { copyProjectSchedule, getExistingProjectsForCopy, getSourceProjectSummary } from '@/lib/copyProjectSchedule';
import { getDefaultCoverUrl, isDefaultCoverUrl } from '@/lib/projectCover';
import { formatDate } from '@/lib/dateUtils';
import {
  initializeProjectFromPreset,
  retryChecklistSetup,
  retryScheduleSetup,
  type PresetInitPhase,
} from '@/lib/projectPresetSetup';
import {
  fetchScheduleTemplateSummary,
  listScheduleTemplatesForNewProject,
  type ScheduleTemplatePickerOption,
  type ScheduleTemplateSummary,
} from '@/lib/projectPresets';
import { resolveDefaultTemplateId } from '@/lib/scheduleTemplateManagement';

// ─── Types ────────────────────────────────────────────────────────────────────

type ProjectType = 'Development' | 'Renovation' | 'Other';
type PaymentStatus = 'upcoming' | 'due' | 'overdue' | 'paid';
type ScheduleSource = 'template' | 'existing' | 'later';
type RenovationScheduleMode = 'light' | 'template' | 'blank';

interface StageSchedule {
  name: string;
  planned_start_date: string;
  planned_end_date: string;
  actual_start_date: string;
  actual_end_date: string;
  status: string;
  delay_reason: string;
}

interface PaymentDraft {
  id: string;
  title: string;
  description: string;
  amount: string;
  due_date: string;
  status: PaymentStatus;
  payment_reference: string;
}

interface ExistingProjectOption {
  id: string;
  project_name: string;
  site_address: string | null;
  project_type: string;
  status: string;
  start_date: string | null;
  target_completion_date: string | null;
  scheduleItemCount: number;
  checklistItemCount: number;
}

interface SourceProjectSummary {
  project_name: string;
  site_address: string | null;
  project_type: string;
  status: string;
  start_date: string | null;
  target_completion_date: string | null;
  itemCount: number;
  stageCount: number;
  linkCount: number;
  checklistCount: number;
  checklistItemCount: number;
}

interface FormState {
  project_name: string;
  site_address: string;
  client_name: string;
  summary: string;
  project_type: ProjectType;
  start_date: string;
  target_completion_date: string;
  subcontractor_email: string;
  subcontractor_name: string;
  subcontractor_company: string;
  client_email: string;
  client_full_name: string;
  cover_image: string;
  cover_is_custom: boolean;
  create_default_schedule: boolean;
  create_default_checklist: boolean;
  link_checklist_to_schedule: boolean;
  include_dependency_links: boolean;
  generate_initial_timeline: boolean;
  schedule_start_date: string;
  schedule_source: ScheduleSource;
  copy_from_project_id: string;
  copy_schedule_items: boolean;
  copy_stages: boolean;
  copy_dependency_links: boolean;
  copy_checklist_items: boolean;
  copy_checklist_mappings: boolean;
  reset_checklist_completion: boolean;
  recalculate_dates: boolean;
  schedule_template_id: string;
  renovation_schedule_mode: RenovationScheduleMode;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_STAGES = ['Demolition', 'Structure', 'Rough-in', 'Fit-out', 'Finishing', 'Handover'];

// STAGE_STATUS_OPTIONS and STEPS are now defined inside the component to support i18n

const PAYMENT_TEMPLATES = [
  { title: 'Deposit', description: 'Initial deposit to confirm project commencement', percent: 10 },
  { title: 'Frame Payment', description: 'Progress payment upon completion of structural framing', percent: 20 },
  { title: 'Rough-in Payment', description: 'Progress payment upon completion of plumbing and electrical rough-in', percent: 20 },
  { title: 'Fit-out Payment', description: 'Progress payment upon completion of fit-out stage', percent: 25 },
  { title: 'Finishing Payment', description: 'Progress payment upon completion of finishing stage', percent: 15 },
  { title: 'Final Payment', description: 'Final payment upon practical completion and handover', percent: 10 },
];

function generateProjectCode(name: string): string {
  const prefix = 'JH';
  const year = new Date().getFullYear();
  const rand = Math.floor(Math.random() * 900) + 100;
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .join('-');
  return `${prefix}-${year}-${rand}-${slug}`.substring(0, 40);
}

function isLightweightProjectType(type: ProjectType): boolean {
  return type === 'Renovation' || type === 'Other';
}

function isBlankCustomProjectType(type: ProjectType): boolean {
  return type === 'Other';
}

const EDGE_FUNCTION_TIMEOUT_MS = 12_000;

async function invokeCreateProjectEdgeFunction(payload: Record<string, unknown>): Promise<void> {
  try {
    await Promise.race([
      supabase.functions.invoke('jphph-create-project', { body: payload }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Edge function timed out')), EDGE_FUNCTION_TIMEOUT_MS),
      ),
    ]);
  } catch (err) {
    console.warn('[NewProject] Edge function (non-fatal):', err instanceof Error ? err.message : String(err));
  }
}

function newPaymentDraft(title = '', description = ''): PaymentDraft {
  return {
    id: `draft-${Date.now()}-${Math.random()}`,
    title,
    description,
    amount: '',
    due_date: '',
    status: 'upcoming',
    payment_reference: '',
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NewProject() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const STAGE_STATUS_OPTIONS = [
    { value: 'not_started', label: t('stages.statusNotStarted') },
    { value: 'in_progress', label: t('stages.statusInProgress') },
    { value: 'completed', label: t('stages.statusCompleted') },
    { value: 'delayed', label: t('stages.statusDelayed') },
    { value: 'on_hold', label: t('status.onHold') },
  ];

  const STEPS = [
    { label: t('newProject.info'), icon: 'ri-file-text-line' },
    { label: t('newProject.type'), icon: 'ri-building-2-line' },
    { label: t('newProject.cover'), icon: 'ri-image-line' },
    { label: t('newProject.team'), icon: 'ri-team-line' },
    { label: t('newProject.stagesStep'), icon: 'ri-list-check-2' },
    { label: t('newProject.scheduleStep'), icon: 'ri-calendar-line' },
    { label: t('newProject.paymentsStep'), icon: 'ri-bank-card-line' },
    { label: t('newProject.review'), icon: 'ri-check-double-line' },
  ];

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');
  const [scheduleSetupWarning, setScheduleSetupWarning] = useState('');
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const [createdProjectType, setCreatedProjectType] = useState<string | null>(null);
  const [scheduleCreated, setScheduleCreated] = useState<boolean | null>(null);
  const [checklistCreated, setChecklistCreated] = useState<boolean | null>(null);
  const [initPhase, setInitPhase] = useState<PresetInitPhase>('idle');
  const [initMessage, setInitMessage] = useState('');
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [retryingSchedule, setRetryingSchedule] = useState(false);
  const [retryingChecklist, setRetryingChecklist] = useState(false);
  const [templateSummary, setTemplateSummary] = useState<ScheduleTemplateSummary | null>(null);
  const [loadingTemplateSummary, setLoadingTemplateSummary] = useState(false);
  const [scheduleTemplateOptions, setScheduleTemplateOptions] = useState<ScheduleTemplatePickerOption[]>([]);
  const [loadingTemplateOptions, setLoadingTemplateOptions] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const coverFileInputRef = useRef<HTMLInputElement>(null);

  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [stageSchedules, setStageSchedules] = useState<Record<string, StageSchedule>>({});

  const [existingProjects, setExistingProjects] = useState<ExistingProjectOption[]>([]);
  const [loadingExistingProjects, setLoadingExistingProjects] = useState(false);
  const [sourceProjectSummary, setSourceProjectSummary] = useState<SourceProjectSummary | null>(null);
  const [loadingSourceSummary, setLoadingSourceSummary] = useState(false);

  const [includePayments, setIncludePayments] = useState(false);
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    project_name: '',
    site_address: '',
    client_name: '',
    summary: '',
    project_type: 'Renovation',
    start_date: '',
    target_completion_date: '',
    subcontractor_email: '',
    subcontractor_name: '',
    subcontractor_company: '',
    client_email: '',
    client_full_name: '',
    cover_image: '',
    cover_is_custom: false,
    create_default_schedule: false,
    create_default_checklist: false,
    link_checklist_to_schedule: false,
    include_dependency_links: false,
    generate_initial_timeline: false,
    schedule_start_date: '',
    schedule_source: 'later',
    copy_from_project_id: '',
    copy_schedule_items: false,
    copy_stages: false,
    copy_dependency_links: false,
    copy_checklist_items: false,
    copy_checklist_mappings: false,
    reset_checklist_completion: true,
    recalculate_dates: false,
    schedule_template_id: '',
    renovation_schedule_mode: 'light',
  });

  const update = (field: keyof FormState, value: string | boolean) =>
    setForm((f) => ({ ...f, [field]: value }));

  const updateProjectType = (type: ProjectType) => {
    setForm((f) => ({
      ...f,
      project_type: type,
      cover_image: f.cover_is_custom ? f.cover_image : getDefaultCoverUrl(type),
      create_default_schedule: type === 'Development',
      create_default_checklist: type === 'Development',
      schedule_source: type === 'Development'
        ? (f.schedule_source === 'later' ? 'template' : f.schedule_source)
        : 'later',
    }));
    if (isLightweightProjectType(type)) {
      setSelectedStages([]);
    } else if (selectedStages.length === 0) {
      setSelectedStages(DEFAULT_STAGES);
    }
  };

  const goToNextStep = () => {
    let next = step + 1;
    if (isLightweightProjectType(form.project_type) && next === 4) next = 5;
    setStep(next);
  };

  const goToPrevStep = () => {
    let prev = step - 1;
    if (isLightweightProjectType(form.project_type) && prev === 4) prev = 3;
    setStep(prev);
  };

  // Sync schedule start date to project start date when project start changes
  useEffect(() => {
    if (form.start_date && !form.schedule_start_date) {
      setForm((f) => ({ ...f, schedule_start_date: f.start_date }));
    }
  }, [form.start_date]);

  // Load existing projects list when on schedule step and source is 'existing'
  useEffect(() => {
    if (step !== 5 || form.schedule_source !== 'existing') return;
    let cancelled = false;
    setLoadingExistingProjects(true);
    getExistingProjectsForCopy().then((list) => {
      if (!cancelled) {
        setExistingProjects(list);
        setLoadingExistingProjects(false);
      }
    });
    return () => { cancelled = true; };
  }, [step, form.schedule_source]);

  // Load source project summary when selection changes
  useEffect(() => {
    if (!form.copy_from_project_id) {
      setSourceProjectSummary(null);
      return;
    }
    let cancelled = false;
    setLoadingSourceSummary(true);
    getSourceProjectSummary(form.copy_from_project_id).then((summary) => {
      if (!cancelled) {
        setSourceProjectSummary(summary);
        setLoadingSourceSummary(false);
      }
    });
    return () => { cancelled = true; };
  }, [form.copy_from_project_id]);

  // Load schedule template options (Development template source or Renovation DB template)
  useEffect(() => {
    const needsOptions =
      step === 5 &&
      form.project_type !== 'Other' &&
      (form.schedule_source === 'template' ||
        (form.project_type === 'Renovation' && form.renovation_schedule_mode === 'template'));

    if (!needsOptions) {
      setScheduleTemplateOptions([]);
      return;
    }

    let cancelled = false;
    setLoadingTemplateOptions(true);
    listScheduleTemplatesForNewProject(form.project_type).then(async (opts) => {
      if (cancelled) return;
      setScheduleTemplateOptions(opts);
      if (opts.length > 0 && !form.schedule_template_id) {
        const def = opts.find((o) => o.is_default) ?? opts[0];
        setForm((f) => ({ ...f, schedule_template_id: def.id }));
      }
      setLoadingTemplateOptions(false);
    });
    return () => { cancelled = true; };
  }, [step, form.schedule_source, form.project_type, form.renovation_schedule_mode]);

  // Load schedule template summary for selected template
  useEffect(() => {
    const needsSummary =
      step === 5 &&
      form.project_type !== 'Other' &&
      (form.schedule_source === 'template' ||
        (form.project_type === 'Renovation' && form.renovation_schedule_mode === 'template'));

    if (!needsSummary) {
      setTemplateSummary(null);
      return;
    }

    let cancelled = false;
    setLoadingTemplateSummary(true);
    const templateId = form.schedule_template_id || undefined;
    fetchScheduleTemplateSummary(form.project_type, templateId).then((summary) => {
      if (!cancelled) {
        setTemplateSummary(summary);
        setLoadingTemplateSummary(false);
      }
    });
    return () => { cancelled = true; };
  }, [
    step,
    form.schedule_source,
    form.project_type,
    form.schedule_template_id,
    form.renovation_schedule_mode,
  ]);

  const toggleStage = (stage: string) => {
    setSelectedStages((prev) =>
      prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage]
    );
  };

  const getStageSchedule = (stageName: string): StageSchedule =>
    stageSchedules[stageName] ?? {
      name: stageName,
      planned_start_date: '',
      planned_end_date: '',
      actual_start_date: '',
      actual_end_date: '',
      status: 'not_started',
      delay_reason: '',
    };

  const updateStageSchedule = (stageName: string, field: keyof StageSchedule, value: string) => {
    setStageSchedules((prev) => ({
      ...prev,
      [stageName]: { ...getStageSchedule(stageName), [field]: value },
    }));
  };

  const addPayment = (template?: typeof PAYMENT_TEMPLATES[0]) => {
    const draft = newPaymentDraft(template?.title ?? '', template?.description ?? '');
    setPayments((prev) => [...prev, draft]);
    setEditingPaymentId(draft.id);
  };

  const updatePayment = (id: string, field: keyof PaymentDraft, value: string) => {
    setPayments((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));
  };

  const removePayment = (id: string) => {
    setPayments((prev) => prev.filter((p) => p.id !== id));
    if (editingPaymentId === id) setEditingPaymentId(null);
  };

  const loadTemplates = () => {
    const drafts = PAYMENT_TEMPLATES.map((t) => newPaymentDraft(t.title, t.description));
    setPayments(drafts);
    setEditingPaymentId(null);
  };

  const canProceed = (): boolean => {
    if (step === 0) return form.project_name.trim().length > 0;
    if (step === 1) return !!form.project_type;
    if (step === 5) {
      if (form.project_type === 'Development' && form.schedule_source === 'existing') {
        return !!form.copy_from_project_id;
      }
      return true;
    }
    return true;
  };

  const handleCoverFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadError(t('error.uploadFailed') + ': ' + 'Please select an image file (JPG, PNG, WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be under 5MB');
      return;
    }

    setUploadingCover(true);
    setUploadError('');

    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `covers/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from('project-files')
        .upload(path, file, { cacheControl: '3600', upsert: false });

      if (uploadErr) {
        console.error('[NewProject] Cover upload error:', uploadErr);
        setUploadError(`Upload failed: ${uploadErr.message}`);
        setUploadingCover(false);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('project-files')
        .getPublicUrl(path);

      const publicUrl = urlData?.publicUrl;
      if (publicUrl) {
        setForm((f) => ({ ...f, cover_image: publicUrl, cover_is_custom: true }));
        console.log('[NewProject] Cover uploaded:', publicUrl);
      }
    } catch (err) {
      console.error('[NewProject] Cover upload exception:', err);
      setUploadError('Upload failed. Please try again or paste a URL below.');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCoverDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleCoverFileSelect(file);
  };

  // ── Direct Supabase insert (no edge function) ──────────────────────────────

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError('');

    const startedAt = performance.now();
    const logTiming = (phase: string) => {
      console.log(`[NewProject] ⏱ ${phase}: ${(performance.now() - startedAt).toFixed(0)}ms`);
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      logTiming('auth session');
      if (!session?.user) {
        setSubmitError(t('error.couldNotLoadData'));
        return;
      }
      const userId = session.user.id;

      const projectCode = generateProjectCode(form.project_name);
      logTiming('create project code');

      const defaultCoverUrl = getDefaultCoverUrl(form.project_type);
      const coverImage = form.cover_is_custom && form.cover_image.trim()
        ? form.cover_image.trim()
        : defaultCoverUrl;

      const projectPayload = {
        project_name: form.project_name.trim(),
        project_code: projectCode,
        project_type: form.project_type,
        client_name: form.client_name.trim() || null,
        site_address: form.site_address.trim() || null,
        summary: form.summary.trim() || null,
        status: 'planning',
        start_date: form.start_date || null,
        target_completion_date: form.target_completion_date || null,
        cover_image: coverImage,
        created_by_user_id: userId,
      };

      const { data: project, error: projectErr } = await supabase
        .from('jphph_projects')
        .insert(projectPayload)
        .select()
        .maybeSingle();

      logTiming('create project record');

      if (projectErr || !project) {
        const msg = projectErr?.message || 'Unknown error inserting project';
        console.error('[NewProject] Project insert FAILED:', projectErr);
        setSubmitError(`Failed to create project: ${msg}`);
        return;
      }

      const newProjectId = project.id;
      const newProjectCode = project.project_code;

      setCreatedProjectId(newProjectId);
      setCreatedProjectType(project.project_type);
      setCreatedUserId(userId);

      const membersToInsert: Array<{ project_id: string; user_id: string; member_role: string; access_scope: string }> = [
        { project_id: newProjectId, user_id: userId, member_role: 'management', access_scope: 'full' },
      ];

      if (form.subcontractor_email.trim()) {
        try {
          const { data: existingSub } = await supabase
            .from('jphph_profiles')
            .select('id')
            .eq('email', form.subcontractor_email.trim())
            .maybeSingle();

          if (existingSub) {
            membersToInsert.push({ project_id: newProjectId, user_id: existingSub.id, member_role: 'subcontractor', access_scope: 'site' });
          }
        } catch (subErr) {
          console.error('[NewProject] Subcontractor lookup error (non-fatal):', subErr);
        }
      }

      if (form.client_email.trim()) {
        try {
          const { data: existingClient } = await supabase
            .from('jphph_profiles')
            .select('id')
            .eq('email', form.client_email.trim())
            .maybeSingle();

          if (existingClient) {
            membersToInsert.push({ project_id: newProjectId, user_id: existingClient.id, member_role: 'client', access_scope: 'client' });
          }
        } catch (clientErr) {
          console.error('[NewProject] Client lookup error (non-fatal):', clientErr);
        }
      }

      const { error: membersErr } = await supabase
        .from('jphph_project_members')
        .insert(membersToInsert);
      logTiming('create project members');
      if (membersErr) {
        console.error('[NewProject] Members insert error (non-fatal):', membersErr);
      }

      const runDeferredOptionalSetup = () => {
        void (async () => {
          const deferredStart = performance.now();
          try {
            if (includePayments && payments.length > 0) {
              const validPayments = payments.filter((p) => p.title.trim() && p.amount);
              if (validPayments.length > 0) {
                const paymentRows = validPayments.map((p, i) => ({
                  project_id: newProjectCode,
                  title: p.title.trim(),
                  description: p.description.trim() || null,
                  amount: parseFloat(p.amount),
                  currency: 'NZD',
                  due_date: p.due_date || null,
                  status: p.status,
                  payment_reference: p.payment_reference.trim() || null,
                  sort_order: i + 1,
                }));
                const { error: paymentsErr } = await supabase
                  .from('jphph_project_payment_schedules')
                  .insert(paymentRows);
                console.log(`[NewProject] ⏱ deferred payments: ${(performance.now() - deferredStart).toFixed(0)}ms`, paymentsErr ?? 'ok');
              }
            }

            if (form.subcontractor_email.trim() || form.client_email.trim()) {
              await invokeCreateProjectEdgeFunction({
                project_id: newProjectId,
                project_code: newProjectCode,
                project_name: form.project_name.trim(),
                project_type: form.project_type,
                subcontractor_email: form.subcontractor_email.trim(),
                subcontractor_name: form.subcontractor_name.trim(),
                subcontractor_company: form.subcontractor_company.trim(),
                client_email: form.client_email.trim(),
                client_full_name: form.client_full_name.trim(),
                client_name: form.client_name.trim(),
                skip_project_creation: true,
              });
              console.log(`[NewProject] ⏱ deferred edge function: ${(performance.now() - deferredStart).toFixed(0)}ms`);
            }
          } catch (deferredErr) {
            console.warn('[NewProject] Deferred setup failed (non-fatal):', deferredErr);
          }
        })();
      };

      if (form.project_type === 'Renovation') {
        if (form.renovation_schedule_mode === 'blank') {
          runDeferredOptionalSetup();
          logTiming('navigation');
          navigate(`/management/projects/${newProjectId}?tab=Schedule&addTask=1`);
          return;
        }

        if (form.renovation_schedule_mode === 'template' && form.schedule_template_id) {
          const scheduleResult = await setupProjectSchedule({
            projectId: newProjectId,
            projectType: 'Renovation',
            createDefaultSchedule: true,
            includeDependencyLinks: true,
            generateInitialTimeline: false,
            scheduleTemplateId: form.schedule_template_id,
          });
          logTiming('create renovation template schedule');
          if (!scheduleResult.success && !scheduleResult.alreadyExists) {
            setScheduleSetupWarning(scheduleResult.warning || scheduleResult.message);
          }
          setScheduleCreated(scheduleResult.success && (scheduleResult.itemsCreated > 0 || scheduleResult.alreadyExists === true));
          runDeferredOptionalSetup();
          logTiming('navigation');
          navigate(`/management/projects/${newProjectId}?tab=Schedule&view=gantt`);
          return;
        }

        const scheduleResult = await setupLightRenovationSchedule(newProjectId);
        logTiming('create light renovation schedule');
        if (!scheduleResult.success && !scheduleResult.alreadyExists) {
          console.warn('[NewProject] Light renovation schedule failed (non-fatal):', scheduleResult.warning || scheduleResult.message);
        }
        setScheduleCreated(scheduleResult.success && (scheduleResult.itemsCreated > 0 || scheduleResult.alreadyExists === true));
        runDeferredOptionalSetup();
        logTiming('navigation');
        navigate(`/management/projects/${newProjectId}?tab=Schedule&view=gantt`);
        return;
      }

      if (isBlankCustomProjectType(form.project_type)) {
        runDeferredOptionalSetup();
        logTiming('navigation');
        navigate(`/management/projects/${newProjectId}?tab=Schedule&addTask=1`);
        return;
      }

      if (selectedStages.length > 0) {
        const stageRows = selectedStages.map((name, i) => {
          const sched = getStageSchedule(name);
          return {
            project_id: newProjectId,
            stage_name: name,
            sort_order: i + 1,
            status: sched.status || 'not_started',
            completion_percent: 0,
            planned_start_date: sched.planned_start_date || null,
            planned_end_date: sched.planned_end_date || null,
            actual_start_date: sched.actual_start_date || null,
            actual_end_date: sched.actual_end_date || null,
            delay_reason: sched.delay_reason || null,
          };
        });
        const { error: stagesErr } = await supabase
          .from('jphph_project_stages')
          .insert(stageRows);
        logTiming('create stages');
        if (stagesErr) {
          console.error('[NewProject] Stages insert error (non-fatal):', stagesErr);
        }
      }

      if (includePayments && payments.length > 0) {
        const validPayments = payments.filter((p) => p.title.trim() && p.amount);
        if (validPayments.length > 0) {
          const paymentRows = validPayments.map((p, i) => ({
            project_id: newProjectCode,
            title: p.title.trim(),
            description: p.description.trim() || null,
            amount: parseFloat(p.amount),
            currency: 'NZD',
            due_date: p.due_date || null,
            status: p.status,
            payment_reference: p.payment_reference.trim() || null,
            sort_order: i + 1,
          }));
          const { error: paymentsErr } = await supabase
            .from('jphph_project_payment_schedules')
            .insert(paymentRows);
          logTiming('create payments');
          if (paymentsErr) {
            console.error('[NewProject] Payments insert error (non-fatal):', paymentsErr);
          }
        }
      } else {
        logTiming('create payments (skipped)');
      }

      if (form.subcontractor_email.trim() || form.client_email.trim()) {
        await invokeCreateProjectEdgeFunction({
          project_id: newProjectId,
          project_code: newProjectCode,
          project_name: form.project_name.trim(),
          project_type: form.project_type,
          subcontractor_email: form.subcontractor_email.trim(),
          subcontractor_name: form.subcontractor_name.trim(),
          subcontractor_company: form.subcontractor_company.trim(),
          client_email: form.client_email.trim(),
          client_full_name: form.client_full_name.trim(),
          client_name: form.client_name.trim(),
          skip_project_creation: true,
        });
        logTiming('edge function');
      }

      let scheduleResult: Awaited<ReturnType<typeof setupProjectSchedule>> | Awaited<ReturnType<typeof copyProjectSchedule>> | null = null;
      let checklistResult: Awaited<ReturnType<typeof setupProjectChecklist>> | null = null;

      if (form.schedule_source === 'template' && form.project_type === 'Development') {
        setInitPhase('schedule');
        setInitMessage(t('newProject.settingUpSchedule'));

        let scheduleTemplateId = form.schedule_template_id || null;
        if (!scheduleTemplateId && form.create_default_schedule) {
          scheduleTemplateId = await resolveDefaultTemplateId(form.project_type);
        }

        const presetResult = await initializeProjectFromPreset({
          projectId: newProjectId,
          projectType: form.project_type,
          userId,
          createDefaultSchedule: form.create_default_schedule,
          createDefaultChecklist: form.create_default_checklist,
          includeDependencyLinks: form.include_dependency_links,
          linkToSchedule: form.link_checklist_to_schedule,
          scheduleTemplateId,
          onPhaseChange: (phase, message) => {
            setInitPhase(phase);
            setInitMessage(message);
          },
        });

        scheduleResult = presetResult.schedule;
        checklistResult = presetResult.checklist;

        if (presetResult.warnings.length > 0) {
          setScheduleSetupWarning(presetResult.warnings.join(' '));
        } else if (!presetResult.success) {
          setScheduleSetupWarning(presetResult.message);
        }
        logTiming('create schedule/checklist (preset)');
      } else if (form.schedule_source === 'existing' && form.copy_from_project_id) {
        setInitPhase('schedule');
        setInitMessage(t('newProject.copyingSchedule'));
        try {
          scheduleResult = await copyProjectSchedule({
            sourceProjectId: form.copy_from_project_id,
            targetProjectId: newProjectId,
            targetProjectType: form.project_type,
            copyScheduleItems: form.copy_schedule_items,
            copyStages: form.copy_stages,
            copyDependencyLinks: form.copy_dependency_links,
            copyChecklistMappings: form.copy_checklist_mappings,
            copyChecklistItems: form.copy_checklist_items,
            resetChecklistCompletion: form.reset_checklist_completion,
            recalculateDates: form.recalculate_dates,
            scheduleStartDate: form.schedule_start_date || undefined,
          });
          if (scheduleResult && 'warning' in scheduleResult && scheduleResult.warning) {
            setScheduleSetupWarning(`${scheduleResult.warning}. You can create the schedule later from the Schedule tab.`);
          }
        } catch (copyErr: unknown) {
          const copyMsg = copyErr instanceof Error ? copyErr.message : String(copyErr);
          setScheduleSetupWarning(`Schedule copy failed unexpectedly: ${copyMsg}. You can create the schedule later from the Schedule tab.`);
        }
        logTiming('create schedule (copy)');

        if (form.create_default_checklist && !form.copy_checklist_items) {
          setInitPhase('checklist');
          setInitMessage(t('newProject.settingUpChecklist'));
          try {
            checklistResult = await setupProjectChecklist({
              projectId: newProjectId,
              projectType: form.project_type,
              createDefaultChecklist: true,
              userId,
            });
            if (!checklistResult.success) {
              setScheduleSetupWarning(checklistResult.error || checklistResult.warning || checklistResult.message);
            }
          } catch (checklistErr: unknown) {
            const checklistMsg = checklistErr instanceof Error ? checklistErr.message : String(checklistErr);
            setScheduleSetupWarning(`Checklist setup failed: ${checklistMsg}. You can create the checklist from the Checklist tab.`);
          }
          logTiming('create checklist');
        }
        setInitPhase('done');
      } else {
        setInitPhase('done');
      }

      const checklistItemsCount = checklistResult?.itemsCreated ?? 0;
      const checklistError = checklistResult?.warning ?? null;

      if (scheduleResult) {
        if ('itemsCreated' in scheduleResult) {
          setScheduleCreated(scheduleResult.success && ((scheduleResult.itemsCreated ?? 0) > 0 || scheduleResult.alreadyExists === true));
        } else if ('itemsCopied' in scheduleResult) {
          setScheduleCreated(scheduleResult.success && (scheduleResult.itemsCopied ?? 0) > 0);
        } else {
          setScheduleCreated(false);
        }
      } else {
        setScheduleCreated(false);
      }

      if (checklistResult) {
        setChecklistCreated(checklistResult.success && ((checklistResult.itemsCreated ?? 0) > 0 || checklistResult.alreadyExists === true));
      } else if (form.schedule_source === 'existing' && form.copy_checklist_items) {
        if (scheduleResult && 'checklistItemsCopied' in scheduleResult) {
          setChecklistCreated((scheduleResult.checklistItemsCopied ?? 0) > 0);
        }
      } else {
        setChecklistCreated(false);
      }

      if (form.create_default_checklist && checklistItemsCount === 0 && !scheduleSetupWarning && form.schedule_source === 'template') {
        const warnMsg = checklistError || 'Checklist creation returned 0 items. You can create it from the Checklist tab.';
        setScheduleSetupWarning(warnMsg);
      }

      setInitPhase('done');
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setSubmitError(`${t('error.pleaseTryAgain')}: ${errMsg}`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleRetrySchedule = async () => {
    if (!createdProjectId || !createdProjectType || createdProjectType !== 'Development') return;
    setRetryingSchedule(true);
    setScheduleSetupWarning('');
    try {
      const result = await retryScheduleSetup(createdProjectId, createdProjectType, form.include_dependency_links);
      setScheduleCreated(result.success && ((result.itemsCreated ?? 0) > 0 || result.alreadyExists === true));
      if (result.warning) setScheduleSetupWarning(result.warning);
      else if (!result.success) setScheduleSetupWarning(result.message);
    } catch (err: unknown) {
      setScheduleSetupWarning(err instanceof Error ? err.message : String(err));
    } finally {
      setRetryingSchedule(false);
    }
  };

  const handleRetryChecklist = async () => {
    if (!createdProjectId || !createdUserId || !createdProjectType) return;
    setRetryingChecklist(true);
    try {
      const result = await retryChecklistSetup(createdProjectId, createdProjectType, createdUserId);
      setChecklistCreated(result.success && ((result.itemsCreated ?? 0) > 0 || result.alreadyExists === true));
      if (!result.success) setScheduleSetupWarning(result.error || result.warning || result.message);
    } catch (err: unknown) {
      setScheduleSetupWarning(err instanceof Error ? err.message : String(err));
    } finally {
      setRetryingChecklist(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const effectiveCover = form.cover_image.trim() || getDefaultCoverUrl(form.project_type);

  return (
    <MgmtLayout title={t('newProject.title')} showBack backTo="/management/projects">
      <div className="px-4 pt-4 pb-10 max-w-2xl mx-auto">

        {/* Step indicator */}
        <div className="flex items-start gap-0 mb-8 overflow-x-auto scrollbar-hide pb-1">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1 min-w-0">
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    i < step
                      ? 'bg-[#E8621A] text-white'
                      : i === step
                      ? 'bg-[#E8621A] text-white ring-4 ring-[#FEF0E7]'
                      : 'bg-gray-100 text-[#9CA3AF]'
                  }`}
                >
                  {i < step ? <i className="ri-check-line text-xs"></i> : i + 1}
                </div>
                <span className={`text-[9px] mt-1 font-medium whitespace-nowrap ${i === step ? 'text-[#E8621A]' : 'text-[#9CA3AF]'}`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 mb-4 transition-colors ${i < step ? 'bg-[#E8621A]' : 'bg-gray-100'}`}></div>
              )}
            </div>
          ))}
        </div>

        {/* ── Step 0: Project Info ── */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.projectInfo')}</h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.basicDetails')}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.projectName')} <span className="text-red-500">*</span></label>
              <input type="text" value={form.project_name} onChange={(e) => update('project_name', e.target.value)} placeholder={t('newProject.projectNamePlaceholder')} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.siteAddress')}</label>
              <input type="text" value={form.site_address} onChange={(e) => update('site_address', e.target.value)} placeholder={t('newProject.siteAddressPlaceholder')} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.clientName')}</label>
              <input type="text" value={form.client_name} onChange={(e) => update('client_name', e.target.value)} placeholder={t('newProject.clientNamePlaceholder')} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.projectDescription')}</label>
              <textarea value={form.summary} onChange={(e) => update('summary', e.target.value)} placeholder={t('newProject.descPlaceholder')} rows={3} maxLength={500} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors resize-none" />
              <p className="text-[10px] text-[#9CA3AF] mt-1 text-right">{form.summary.length}/500</p>
            </div>
          </div>
        )}

        {/* ── Step 1: Project Type ── */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.projectType')} <span className="text-red-500">*</span></h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.selectType')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(['Development', 'Renovation', 'Other'] as ProjectType[]).map((type) => (
                <button key={type} type="button" onClick={() => updateProjectType(type)}
                  className={`relative flex flex-col items-start p-5 rounded-2xl border-2 text-left cursor-pointer transition-all ${form.project_type === type ? 'border-[#E8621A] bg-[#FEF0E7]' : 'border-gray-200 bg-white hover:border-[#E8621A]/40'}`}>
                  {form.project_type === type && (
                    <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#E8621A] flex items-center justify-center">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                  <div className="w-12 h-12 rounded-xl bg-[#FEF0E7] flex items-center justify-center mb-3">
                    <i className={`${type === 'Development' ? 'ri-building-4-line' : type === 'Renovation' ? 'ri-hammer-line' : 'ri-more-line'} text-[#E8621A] text-2xl`}></i>
                  </div>
                  <div className="font-bold text-[#1A1A1A] text-base mb-1">
                    {type === 'Development' ? t('newProject.development') : type === 'Renovation' ? t('newProject.renovation') : t('newProject.other')}
                  </div>
                  <div className="text-[#6B7280] text-xs leading-relaxed">
                    {type === 'Development' ? t('newProject.developmentDesc') : type === 'Renovation' ? t('newProject.renovationDesc') : t('newProject.otherDesc')}
                  </div>
                </button>
              ))}
            </div>

            {/* Default cover preview for selected type */}
            <div className="mt-4">
              <p className="text-xs font-semibold text-[#374151] mb-2">{t('newProject.defaultCoverPhoto')}</p>
              <div className="h-36 rounded-xl overflow-hidden relative">
                <img
                  src={getDefaultCoverUrl(form.project_type)}
                  alt={`${form.project_type} default cover`}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-2 left-3 right-3 flex items-center gap-2">
                  <span className="text-white text-xs font-bold">{t('newProject.defaultCoverLabel', { type: form.project_type })}</span>
                  {form.cover_is_custom && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#059669] text-white font-semibold">
                      {t('newProject.customPhotoSet')}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-1.5">
                {t('newProject.autoUsedUnlessCustom')}
              </p>
            </div>
          </div>
        )}

        {/* ── Step 5: Schedule (setup + optional timeline) ── */}
        {step === 5 && (
          <div className="space-y-5">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.scheduleStep')}</h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.scheduleStepDesc')}</p>
            </div>

            {/* Schedule Source selector */}
            {form.project_type === 'Renovation' ? (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <p className="text-sm font-bold text-[#1A1A1A] mb-3">{t('newProject.renovationScheduleMode')}</p>
                  <div className="flex flex-col gap-2">
                    {([
                      { value: 'light' as RenovationScheduleMode, label: t('newProject.renovationModeLight'), icon: 'ri-list-check' },
                      { value: 'template' as RenovationScheduleMode, label: t('newProject.renovationModeTemplate'), icon: 'ri-calendar-todo-line' },
                      { value: 'blank' as RenovationScheduleMode, label: t('newProject.renovationModeBlank'), icon: 'ri-edit-line' },
                    ]).map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-3 rounded-xl px-4 py-3 cursor-pointer border transition-all ${
                          form.renovation_schedule_mode === opt.value
                            ? 'bg-[#FEF0E7] border-[#E8621A]/30'
                            : 'bg-white border-gray-200 hover:border-[#E8621A]/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="renovation_schedule_mode"
                          checked={form.renovation_schedule_mode === opt.value}
                          onChange={() => update('renovation_schedule_mode', opt.value)}
                          className="accent-[#E8621A] w-4 h-4 flex-shrink-0"
                        />
                        <i className={`${opt.icon} text-[#E8621A]`}></i>
                        <span className="text-sm font-medium text-[#374151]">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {form.renovation_schedule_mode === 'light' && (
                  <div className="bg-[#F9FAFB] rounded-2xl border border-gray-100 p-4">
                    <p className="text-xs font-semibold text-[#374151] mb-2">{t('newProject.lightRenovationTasksHeading')}</p>
                    <ol className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {LIGHT_RENOVATION_SCHEDULE_TASKS.map((task, i) => (
                        <li key={task} className="text-xs text-[#6B7280] flex items-start gap-2">
                          <span className="text-[#E8621A] font-bold flex-shrink-0">{i + 1}.</span>
                          <span>{task}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {form.renovation_schedule_mode === 'template' && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
                    <label className="block text-sm font-semibold text-[#374151]">{t('newProject.selectScheduleTemplate')}</label>
                    {loadingTemplateOptions ? (
                      <p className="text-xs text-[#9CA3AF] flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        {t('newProject.loadingPresetInfo')}
                      </p>
                    ) : scheduleTemplateOptions.length === 0 ? (
                      <p className="text-xs text-[#92400E]">{t('newProject.noPresetConfigured')}</p>
                    ) : (
                      <select
                        value={form.schedule_template_id}
                        onChange={(e) => update('schedule_template_id', e.target.value)}
                        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white cursor-pointer"
                      >
                        {scheduleTemplateOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                            {opt.is_default ? ` (${t('templates.defaultBadge')})` : ''}
                            {` — ${opt.item_count} ${t('newProject.tasksLabel')}`}
                          </option>
                        ))}
                      </select>
                    )}
                    {templateSummary && (
                      <p className="text-xs text-[#047857]">
                        {t('newProject.presetItemCount', { count: templateSummary.itemCount })}
                        {templateSummary.linkCount > 0
                          ? ` · ${t('newProject.presetLinkCount', { count: templateSummary.linkCount })}`
                          : ''}
                      </p>
                    )}
                  </div>
                )}

                {form.renovation_schedule_mode === 'blank' && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-4">
                    <p className="text-[#9CA3AF] text-xs leading-relaxed">{t('newProject.renovationModeBlankDesc')}</p>
                  </div>
                )}
              </div>
            ) : form.project_type === 'Other' ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#F9FAFB] flex items-center justify-center flex-shrink-0">
                    <i className="ri-more-line text-[#6B7280] text-xl"></i>
                  </div>
                  <div>
                    <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.blankCustomTitle')}</p>
                    <p className="text-[#9CA3AF] text-xs mt-1 leading-relaxed">{t('newProject.blankCustomScheduleDesc')}</p>
                  </div>
                </div>
              </div>
            ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <p className="text-sm font-bold text-[#1A1A1A] mb-3">{t('newProject.scheduleSource')}</p>
              <div className="flex flex-col gap-2">
                {([
                  { value: 'template' as ScheduleSource, label: t('newProject.useDefaultTemplate'), icon: 'ri-calendar-todo-line' },
                  { value: 'existing' as ScheduleSource, label: t('newProject.copyFromExisting'), icon: 'ri-folders-line' },
                  { value: 'later' as ScheduleSource, label: t('newProject.createScheduleLater'), icon: 'ri-time-line' },
                ]).map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 cursor-pointer border transition-all ${form.schedule_source === opt.value ? 'bg-[#FEF0E7] border-[#E8621A]/30' : 'bg-white border-gray-200 hover:border-[#E8621A]/30'}`}
                  >
                    <input
                      type="radio"
                      name="schedule_source"
                      checked={form.schedule_source === opt.value}
                      onChange={() => {
                        setForm((f) => ({
                          ...f,
                          schedule_source: opt.value,
                          create_default_schedule: opt.value === 'template',
                          create_default_checklist: opt.value !== 'later',
                          link_checklist_to_schedule: opt.value === 'template',
                          copy_schedule_items: opt.value === 'existing',
                          copy_stages: opt.value === 'existing',
                          copy_dependency_links: opt.value === 'existing',
                          copy_checklist_items: opt.value === 'existing',
                          copy_checklist_mappings: opt.value === 'existing',
                          reset_checklist_completion: opt.value === 'existing',
                          recalculate_dates: opt.value === 'existing',
                          generate_initial_timeline: opt.value === 'template',
                        }));
                        setSourceProjectSummary(null);
                      }}
                      className="accent-[#E8621A] w-4 h-4 flex-shrink-0"
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-8 h-8 rounded-lg bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                        <i className={`${opt.icon} text-[#E8621A] text-sm`}></i>
                      </div>
                      <span className="text-sm font-medium text-[#374151]">{opt.label}</span>
                    </div>
                    {form.schedule_source === opt.value && <i className="ri-check-line text-[#E8621A] text-sm"></i>}
                  </label>
                ))}
              </div>
            </div>
            )}

            {/* ── TEMPLATE branch (Development only) ── */}
            {form.project_type === 'Development' && form.schedule_source === 'template' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <label className="block text-sm font-semibold text-[#374151] mb-2">{t('newProject.selectScheduleTemplate')}</label>
                  {loadingTemplateOptions ? (
                    <p className="text-xs text-[#9CA3AF] flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      {t('newProject.loadingPresetInfo')}
                    </p>
                  ) : scheduleTemplateOptions.length === 0 ? (
                    <p className="text-xs text-[#92400E]">{t('newProject.noPresetConfigured')}</p>
                  ) : (
                    <select
                      value={form.schedule_template_id}
                      onChange={(e) => update('schedule_template_id', e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white cursor-pointer"
                    >
                      {scheduleTemplateOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                          {opt.is_default ? ` (${t('templates.defaultBadge')})` : ''}
                          {` — ${opt.item_count} ${t('newProject.tasksLabel')}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {templateSummary && (
                  <div className="bg-[#F0FDF4] rounded-xl p-4 flex items-start gap-3 border border-[#86EFAC]/40">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-check-line text-[#059669] text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[#065F46] text-sm font-semibold">{templateSummary.name}</p>
                      <p className="text-[#047857] text-xs mt-0.5">
                        {t('newProject.presetItemCount', { count: templateSummary.itemCount })}
                        {templateSummary.linkCount > 0 ? ` · ${t('newProject.presetLinkCount', { count: templateSummary.linkCount })}` : ''}
                        {templateSummary.source === 'builtin' ? ` · ${t('newProject.presetBuiltIn')}` : ''}
                      </p>
                      {templateSummary.description && (
                        <p className="text-[#6B7280] text-xs mt-1">{templateSummary.description}</p>
                      )}
                    </div>
                  </div>
                )}
                {loadingTemplateSummary && form.project_type !== 'Other' && (
                  <div className="bg-[#F9FAFB] rounded-xl p-3 text-xs text-[#9CA3AF] flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t('newProject.loadingPresetInfo')}
                  </div>
                )}
                {!loadingTemplateSummary && form.project_type !== 'Other' && !templateSummary && (
                  <div className="bg-[#FEF0E7] rounded-xl p-4 flex items-start gap-3">
                    <i className="ri-information-line text-[#E8621A] text-sm mt-0.5"></i>
                    <p className="text-[#92400E] text-xs">{t('newProject.noPresetConfigured')}</p>
                  </div>
                )}

                {/* Schedule toggle */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-todo-line text-[#E8621A] text-xl"></i>
                      </div>
                      <div>
                        <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.createDefaultSchedule')}</p>
                        <p className="text-[#9CA3AF] text-xs mt-0.5">
                          {form.project_type === 'Development'
                            ? t('newProject.copyDevTemplate')
                            : form.project_type === 'Renovation'
                            ? t('newProject.copyRenovationTemplate')
                            : t('newProject.copyActiveTemplate')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => update('create_default_schedule', !form.create_default_schedule)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.create_default_schedule ? 'bg-[#E8621A]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.create_default_schedule ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>
                </div>

                {/* Checklist toggle */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                        <i className="ri-list-check-2 text-[#059669] text-xl"></i>
                      </div>
                      <div>
                        <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.createDefaultChecklist')}</p>
                        <p className="text-[#9CA3AF] text-xs mt-0.5">{t('newProject.createDefaultChecklistDesc')}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => update('create_default_checklist', !form.create_default_checklist)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.create_default_checklist ? 'bg-[#059669]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.create_default_checklist ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>
                </div>

                {/* Link checklist to schedule toggle */}
                {form.create_default_checklist && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
                          <i className="ri-link text-[#0EA5E9] text-xl"></i>
                        </div>
                        <div>
                          <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.linkChecklistToSchedule')}</p>
                          <p className="text-[#9CA3AF] text-xs mt-0.5">{t('newProject.linkChecklistToScheduleDesc')}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => update('link_checklist_to_schedule', !form.link_checklist_to_schedule)}
                        className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.link_checklist_to_schedule ? 'bg-[#0EA5E9]' : 'bg-gray-200'}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.link_checklist_to_schedule ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                      </button>
                    </div>
                  </div>
                )}

                {form.create_default_schedule && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-2xl border border-gray-100 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                            <i className="ri-link text-[#059669] text-xl"></i>
                          </div>
                          <div>
                            <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.includeDependencyLinks')}</p>
                            <p className="text-[#9CA3AF] text-xs mt-0.5">{t('newProject.includeDependencyLinksDesc')}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => update('include_dependency_links', !form.include_dependency_links)}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.include_dependency_links ? 'bg-[#059669]' : 'bg-gray-200'}`}
                        >
                          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.include_dependency_links ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                        </button>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-100 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
                            <i className="ri-time-line text-[#0EA5E9] text-xl"></i>
                          </div>
                          <div>
                            <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.generateInitialTimeline')}</p>
                            <p className="text-[#9CA3AF] text-xs mt-0.5">{t('newProject.generateTimelineDesc')}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => update('generate_initial_timeline', !form.generate_initial_timeline)}
                          className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.generate_initial_timeline ? 'bg-[#0EA5E9]' : 'bg-gray-200'}`}
                        >
                          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.generate_initial_timeline ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                        </button>
                      </div>
                    </div>

                    {form.generate_initial_timeline && (
                      <div>
                        <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.scheduleStartDate')}</label>
                        <input
                          type="date"
                          value={form.schedule_start_date}
                          onChange={(e) => update('schedule_start_date', e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors"
                        />
                        {form.schedule_start_date && form.start_date && form.schedule_start_date !== form.start_date && (
                          <button
                            onClick={() => update('schedule_start_date', form.start_date)}
                            className="mt-2 text-xs text-[#E8621A] cursor-pointer hover:underline"
                          >
                            {t('newProject.useProjectStartDate')} ({form.start_date})
                          </button>
                        )}
                      </div>
                    )}

                    <div className="bg-[#F9FAFB] rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-calendar-check-line text-[#9CA3AF] text-sm"></i>
                        <span className="text-xs font-semibold text-[#374151]">{t('newProject.workingDays')}</span>
                      </div>
                      <p className="text-[#6B7280] text-xs leading-relaxed">
                        {t('newProject.workingDaysDesc')}
                      </p>
                    </div>
                  </div>
                )}

                {!form.create_default_schedule && !form.create_default_checklist && (
                  <div className="bg-[#F9FAFB] rounded-xl p-4 flex items-start gap-2">
                    <i className="ri-information-line text-[#9CA3AF] text-sm mt-0.5"></i>
                    <p className="text-[#6B7280] text-xs leading-relaxed">
                      {t('newProject.noTemplateScheduleChecklist')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── EXISTING PROJECT branch ── */}
            {form.project_type === 'Development' && form.schedule_source === 'existing' && (
              <div className="space-y-4">
                {/* Project selector */}
                <div>
                  <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.selectProjectToCopy')}</label>
                  <div className="relative">
                    {loadingExistingProjects ? (
                      <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-[#9CA3AF]">
                        <i className="ri-loader-4-line animate-spin text-sm"></i>
                        {t('newProject.loadingProjects')}
                      </div>
                    ) : (
                      <select
                        value={form.copy_from_project_id}
                        onChange={(e) => update('copy_from_project_id', e.target.value)}
                        className="w-full min-w-0 max-w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors appearance-none"
                        style={{ boxSizing: 'border-box' }}
                      >
                        <option value="">{t('newProject.chooseExistingProject')}</option>
                        {existingProjects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.project_name}
                            {p.site_address ? ` — ${p.site_address}` : ''}
                            {' — '}
                            {p.project_type}
                            {' — '}
                            {p.scheduleItemCount} {t('newProject.scheduleItems')}
                            {p.checklistItemCount > 0 ? `, ${p.checklistItemCount} ${t('newProject.checklistItems')}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {!loadingExistingProjects && (
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-lg pointer-events-none"></i>
                    )}
                  </div>
                  {form.schedule_source === 'existing' && !form.copy_from_project_id && existingProjects.length > 0 && (
                    <p className="text-[#F59E0B] text-xs mt-1.5 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {t('newProject.pleaseSelectProject')}
                    </p>
                  )}
                  {existingProjects.length === 0 && !loadingExistingProjects && (
                    <p className="text-[#9CA3AF] text-xs mt-1.5">{t('newProject.noExistingProjects')}</p>
                  )}
                </div>

                {/* Source project summary */}
                {form.copy_from_project_id && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-file-list-3-line text-[#E8621A] text-sm"></i>
                      <span className="text-sm font-bold text-[#1A1A1A]">{t('newProject.sourceProjectSummary')}</span>
                    </div>
                    {loadingSourceSummary ? (
                      <div className="flex items-center gap-2 text-[#9CA3AF] text-sm">
                        <i className="ri-loader-4-line animate-spin"></i> {t('newProject.loadingSummary')}
                      </div>
                    ) : sourceProjectSummary ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between py-1 border-b border-gray-50">
                          <span className="text-[#9CA3AF] text-xs">{t('newProject.scheduleItemsLabel')}</span>
                          <span className="text-[#374151] text-xs font-bold">{sourceProjectSummary.itemCount}</span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-gray-50">
                          <span className="text-[#9CA3AF] text-xs">{t('newProject.stagesLabel2')}</span>
                          <span className="text-[#374151] text-xs font-bold">{sourceProjectSummary.stageCount}</span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-gray-50">
                          <span className="text-[#9CA3AF] text-xs">{t('newProject.dependencyLinks')}</span>
                          <span className="text-[#374151] text-xs font-bold">
                            {sourceProjectSummary.linkCount > 0 ? `${sourceProjectSummary.linkCount} ${t('newProject.available')}` : t('newProject.notAvailable')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-gray-50">
                          <span className="text-[#9CA3AF] text-xs">{t('newProject.originalDateRange')}</span>
                          <span className="text-[#374151] text-xs font-bold">
                            {sourceProjectSummary.start_date && sourceProjectSummary.target_completion_date
                              ? `${sourceProjectSummary.start_date} → ${sourceProjectSummary.target_completion_date}`
                              : t('newProject.notAvailable')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-gray-50">
                          <span className="text-[#9CA3AF] text-xs">{t('newProject.checklistsLabel')}</span>
                          <span className="text-[#374151] text-xs font-bold">{sourceProjectSummary.checklistCount}</span>
                        </div>
                        <div className="flex items-center justify-between py-1">
                          <span className="text-[#9CA3AF] text-xs">{t('newProject.checklistItemsLabel')}</span>
                          <span className="text-[#374151] text-xs font-bold">{sourceProjectSummary.checklistItemCount}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[#9CA3AF] text-xs">{t('newProject.couldNotLoadSummary')}</p>
                    )}
                  </div>
                )}

                {/* Copy options */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
                  <p className="text-sm font-bold text-[#1A1A1A]">{t('newProject.copyOptions')}</p>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#374151]">{t('newProject.copyScheduleItems')}</span>
                    <button
                      onClick={() => update('copy_schedule_items', !form.copy_schedule_items)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.copy_schedule_items ? 'bg-[#059669]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.copy_schedule_items ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#374151]">{t('newProject.copyStages')}</span>
                    <button
                      onClick={() => update('copy_stages', !form.copy_stages)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.copy_stages ? 'bg-[#059669]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.copy_stages ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#374151]">{t('newProject.copyDependencyLinks')}</span>
                    <button
                      onClick={() => update('copy_dependency_links', !form.copy_dependency_links)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.copy_dependency_links ? 'bg-[#059669]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.copy_dependency_links ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-[#374151]">{t('newProject.copyChecklistItems')}</span>
                      <p className="text-[#9CA3AF] text-[10px]">{t('newProject.copyChecklistItemsDesc')}</p>
                    </div>
                    <button
                      onClick={() => update('copy_checklist_items', !form.copy_checklist_items)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.copy_checklist_items ? 'bg-[#059669]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.copy_checklist_items ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>

                  {form.copy_checklist_items && (
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm text-[#374151]">{t('newProject.resetChecklistCompletion')}</span>
                        <p className="text-[#9CA3AF] text-[10px]">{t('newProject.resetChecklistCompletionDesc')}</p>
                      </div>
                      <button
                        onClick={() => update('reset_checklist_completion', !form.reset_checklist_completion)}
                        className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.reset_checklist_completion ? 'bg-[#059669]' : 'bg-gray-200'}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.reset_checklist_completion ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                      </button>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-[#374151]">{t('newProject.copyChecklistMappings')}</span>
                      <p className="text-[#9CA3AF] text-[10px]">{t('newProject.copyChecklistMappingsDesc')}</p>
                    </div>
                    <button
                      onClick={() => update('copy_checklist_mappings', !form.copy_checklist_mappings)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.copy_checklist_mappings ? 'bg-[#059669]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.copy_checklist_mappings ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>
                </div>

                {/* Date handling */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
                  <p className="text-sm font-bold text-[#1A1A1A]">{t('newProject.dateHandling')}</p>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-[#374151]">{t('newProject.generateTimelineFromStart')}</span>
                      <p className="text-[#9CA3AF] text-[10px]">{t('newProject.preservesDurations')}</p>
                    </div>
                    <button
                      onClick={() => update('recalculate_dates', !form.recalculate_dates)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.recalculate_dates ? 'bg-[#0EA5E9]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.recalculate_dates ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>

                  {form.recalculate_dates && (
                    <div>
                      <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.newProjectStartDate')}</label>
                      <input
                        type="date"
                        value={form.schedule_start_date || form.start_date}
                        onChange={(e) => update('schedule_start_date', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors"
                      />
                      {form.start_date && (
                        <button
                          onClick={() => update('schedule_start_date', form.start_date)}
                          className="mt-1.5 text-xs text-[#E8621A] cursor-pointer hover:underline"
                        >
                          {t('newProject.useProjectStartDate')} ({form.start_date})
                        </button>
                      )}
                    </div>
                  )}

                  {!form.recalculate_dates && (
                    <p className="text-[#9CA3AF] text-xs leading-relaxed">{t('newProject.itemsCopiedWithDurationsOnly')}</p>
                  )}
                </div>

                <div className="bg-[#FEF0E7] rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-[#F59E0B] text-sm mt-0.5"></i>
                  <p className="text-[#92400E] text-xs leading-relaxed">{t('newProject.completionStatusNotCopied')}</p>
                </div>
              </div>
            )}

            {/* ── CREATE LATER branch ── */}
            {(form.project_type === 'Development' && form.schedule_source === 'later') || form.project_type === 'Other' ? (
              <div className="space-y-4">
                <div className="bg-[#FEF0E7] rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-error-warning-line text-[#F59E0B] text-sm mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="text-[#92400E] text-sm font-semibold">{t('newProject.noScheduleWillBeCreated')}</p>
                    <p className="text-[#B45309] text-xs mt-1 leading-relaxed">{t('newProject.createOrImportLater')}</p>
                  </div>
                </div>

                {/* Still offer default checklist */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                        <i className="ri-list-check-2 text-[#059669] text-xl"></i>
                      </div>
                      <div>
                        <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.createDefaultChecklist')}</p>
                        <p className="text-[#9CA3AF] text-xs mt-0.5">{t('newProject.createDefaultChecklistLaterDesc')}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => update('create_default_checklist', !form.create_default_checklist)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.create_default_checklist ? 'bg-[#059669]' : 'bg-gray-200'}`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.create_default_checklist ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>
                </div>

                <div className="bg-[#F9FAFB] rounded-xl p-4 flex items-start gap-2">
                  <i className="ri-information-line text-[#9CA3AF] text-sm mt-0.5"></i>
                  <p className="text-[#6B7280] text-xs leading-relaxed">{t('newProject.afterCreationScheduleTab')}</p>
                </div>
              </div>
            ) : null}

            {/* Optional project timeline */}
            <div className="pt-2 border-t border-gray-100 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-[#1A1A1A]">{t('newProject.overallSchedule')}</h3>
                <p className="text-xs text-[#9CA3AF] mt-0.5">{t('newProject.optionalSetTimeline')}</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.startDate')}</label>
                    <input type="date" value={form.start_date} onChange={(e) => update('start_date', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.estCompletion')}</label>
                    <input type="date" value={form.target_completion_date} onChange={(e) => update('target_completion_date', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors" />
                  </div>
                </div>
                {form.start_date && form.target_completion_date && (
                  <div className="bg-[#F9FAFB] rounded-xl p-3 flex items-center gap-2">
                    <i className="ri-time-line text-[#E8621A] text-sm"></i>
                    <span className="text-sm text-[#374151] font-medium">
                      {(() => {
                        const diffDays = Math.round((new Date(form.target_completion_date).getTime() - new Date(form.start_date).getTime()) / 86400000);
                        const months = Math.floor(diffDays / 30);
                        const weeks = Math.floor(diffDays / 7);
                        if (months >= 2) return t('newProject.monthsDuration', { n: months });
                        if (weeks >= 1) return weeks === 1 ? t('newProject.weekDuration', { n: weeks }) : t('newProject.weeksDuration', { n: weeks });
                        return diffDays === 1 ? t('newProject.dayDuration', { n: diffDays }) : t('newProject.daysDuration', { n: diffDays });
                      })()}
                    </span>
                  </div>
                )}
              </div>
              <div className="bg-[#F9FAFB] rounded-xl p-3 flex items-start gap-2">
                <i className="ri-information-line text-[#9CA3AF] text-sm mt-0.5"></i>
                <p className="text-[#6B7280] text-xs leading-relaxed">{t('newProject.stageDatesFromEdit')}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Cover Photo ── */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.coverPhoto')}</h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.uploadOrLeaveBlank')}</p>
            </div>

            {/* Preview */}
            <div className="relative h-52 rounded-2xl overflow-hidden bg-gray-100">
              <img
                src={effectiveCover}
                alt={t('newProject.coverPreview')}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-3 left-3 right-3">
                <div className="text-white font-bold text-sm leading-tight">{form.project_name || t('newProject.projectNamePreview')}</div>
                <div className="text-white/60 text-xs mt-0.5">{form.site_address || t('newProject.siteAddressPreview')}</div>
              </div>
              {form.cover_is_custom ? (
                <div className="absolute top-3 right-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#059669]/80 text-white backdrop-blur-sm">{t('newProject.customPhoto')}</span>
                </div>
              ) : (
                <div className="absolute top-3 right-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/40 text-white backdrop-blur-sm">{t('newProject.defaultCover')}</span>
                </div>
              )}
            </div>

            {/* Upload area */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleCoverDrop}
              onClick={() => coverFileInputRef.current?.click()}
              className="relative rounded-2xl border-2 border-dashed border-gray-200 bg-[#F9FAFB] p-6 text-center cursor-pointer hover:border-[#E8621A] hover:bg-[#FEF0E7]/30 transition-all"
            >
              <input
                ref={coverFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleCoverFileSelect(file);
                  e.target.value = '';
                }}
                className="hidden"
              />
              {uploadingCover ? (
                <div className="flex flex-col items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
                  <p className="text-sm text-[#374151] font-medium">{t('newProject.uploading')}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-[#FEF0E7] flex items-center justify-center">
                    <i className="ri-upload-cloud-2-line text-[#E8621A] text-2xl"></i>
                  </div>
                  <p className="text-sm font-semibold text-[#374151]">{t('newProject.clickOrDragDrop')}</p>
                  <p className="text-xs text-[#9CA3AF]">{t('newProject.jpgPngWebp')}</p>
                </div>
              )}
            </div>

            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-red-600 text-sm">{uploadError}</p>
              </div>
            )}

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-[#9CA3AF] font-medium">{t('newProject.orPasteUrl')}</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* URL input */}
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-1.5">{t('newProject.coverPhotoUrl')}</label>
              <input
                ref={coverInputRef}
                type="url"
                value={form.cover_image}
                onChange={(e) => {
                const value = e.target.value;
                setForm((f) => ({ ...f, cover_image: value, cover_is_custom: !!value.trim() }));
              }}
                placeholder={t('newProject.coverUrlPlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:border-[#E8621A] transition-colors"
              />
              {form.cover_image && (
                <button
                  onClick={() => setForm((f) => ({ ...f, cover_image: getDefaultCoverUrl(f.project_type), cover_is_custom: false }))}
                  className="mt-2 text-xs text-[#9CA3AF] cursor-pointer hover:text-[#E8621A] transition-colors"
                >
                  {t('newProject.resetToDefault')}
                </button>
              )}
            </div>

            {/* Default info */}
            <div className="bg-[#F9FAFB] rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-[#374151]">{t('newProject.defaultCoversByType')}</p>
              <div className="flex items-start gap-2">
                <i className="ri-building-4-line text-[#E8621A] text-sm mt-0.5"></i>
                <p className="text-[#6B7280] text-xs leading-relaxed">{t('newProject.developmentCoverDesc')}</p>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-hammer-line text-[#E8621A] text-sm mt-0.5"></i>
                <p className="text-[#6B7280] text-xs leading-relaxed">{t('newProject.renovationCoverDesc')}</p>
              </div>
              <p className="text-[#9CA3AF] text-xs mt-1">{t('newProject.changeCoverAnytime')}</p>
            </div>
          </div>
        )}

        {/* ── Step 3: Team ── */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.teamAccess')}</h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.optionalAddTeam')}</p>
            </div>

            <div className="bg-[#F9FAFB] rounded-xl p-3 flex items-start gap-2">
              <i className="ri-information-line text-[#9CA3AF] text-sm mt-0.5"></i>
              <p className="text-[#6B7280] text-xs leading-relaxed">{t('newProject.ifEmailHasAccount')}</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
                  <i className="ri-tools-line text-[#059669] text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-[#1A1A1A]">{t('newProject.subcontractor')} <span className="text-[#9CA3AF] font-normal text-xs">{t('newProject.optional')}</span></div>
                  <div className="text-[11px] text-[#9CA3AF]">{t('newProject.existingAccountsAdded')}</div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.emailAddress')}</label>
                  <input type="email" value={form.subcontractor_email} onChange={(e) => update('subcontractor_email', e.target.value)} placeholder="subcontractor@example.com" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-[#F9FAFB] focus:outline-none focus:border-[#059669] transition-colors" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.contactName')}</label>
                    <input type="text" value={form.subcontractor_name} onChange={(e) => update('subcontractor_name', e.target.value)} placeholder="e.g. Site Foreman" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-[#F9FAFB] focus:outline-none focus:border-[#059669] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.company')}</label>
                    <input type="text" value={form.subcontractor_company} onChange={(e) => update('subcontractor_company', e.target.value)} placeholder="e.g. Auckland Electrical" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-[#F9FAFB] focus:outline-none focus:border-[#059669] transition-colors" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#FEF0E7] flex items-center justify-center">
                  <i className="ri-user-line text-[#E8621A] text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-[#1A1A1A]">{t('newProject.client')} <span className="text-[#9CA3AF] font-normal text-xs">{t('newProject.optional')}</span></div>
                  <div className="text-[11px] text-[#9CA3AF]">{t('newProject.existingAccountsAdded')}</div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.emailAddress')}</label>
                  <input type="email" value={form.client_email} onChange={(e) => update('client_email', e.target.value)} placeholder="client@example.com" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-[#F9FAFB] focus:outline-none focus:border-[#E8621A] transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.fullName')}</label>
                  <input type="text" value={form.client_full_name} onChange={(e) => update('client_full_name', e.target.value)} placeholder="e.g. Yamamoto Family" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-[#F9FAFB] focus:outline-none focus:border-[#E8621A] transition-colors" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Stages ── */}
        {step === 4 && (
          <div className="space-y-3">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.stages')}</h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.selectStages')}</p>
            </div>
            {DEFAULT_STAGES.map((stage, i) => (
              <label key={stage} className={`flex items-center gap-3 rounded-xl px-4 py-3.5 cursor-pointer border transition-all ${selectedStages.includes(stage) ? 'bg-[#FEF0E7] border-[#E8621A]/30' : 'bg-white border-gray-200 hover:border-[#E8621A]/30'}`}>
                <input type="checkbox" checked={selectedStages.includes(stage)} onChange={() => toggleStage(stage)} className="accent-[#E8621A] w-4 h-4 flex-shrink-0" />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-5 h-5 rounded-full bg-[#E8621A]/10 text-[#E8621A] text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <span className="text-sm font-medium text-[#374151]">{stage}</span>
                </div>
                {selectedStages.includes(stage) && <i className="ri-check-line text-[#E8621A] text-sm"></i>}
              </label>
            ))}
            <p className="text-[#9CA3AF] text-xs pt-1">{t('newProject.stagesSelected', { count: selectedStages.length })}</p>
          </div>
        )}

        {/* ── Step 6: Payment Schedule (OPTIONAL) ── */}
        {step === 6 && (
          <div className="space-y-4">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.paymentSchedule')}</h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.paymentScheduleDesc')}</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                    <i className="ri-bank-card-line text-[#E8621A] text-xl"></i>
                  </div>
                  <div>
                    <p className="text-[#1A1A1A] font-bold text-sm">{t('newProject.setUpPaymentNow')}</p>
                    <p className="text-[#9CA3AF] text-xs mt-0.5">{t('newProject.addLaterFromEdit')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setIncludePayments((v) => !v)}
                  className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${includePayments ? 'bg-[#E8621A]' : 'bg-gray-200'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${includePayments ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                </button>
              </div>
            </div>

            {includePayments && (
              <>
                {payments.length === 0 && (
                  <div className="bg-[#F9FAFB] rounded-2xl border border-dashed border-gray-200 p-5 text-center">
                    <div className="w-12 h-12 rounded-full bg-[#FEF0E7] flex items-center justify-center mx-auto mb-3">
                      <i className="ri-bank-card-line text-[#E8621A] text-xl"></i>
                    </div>
                    <p className="text-[#374151] font-semibold text-sm mb-1">{t('newProject.noPaymentsAdded')}</p>
                    <p className="text-[#9CA3AF] text-xs mb-4">{t('newProject.startFromTemplate')}</p>
                    <div className="flex gap-2 justify-center flex-wrap">
                      <button onClick={loadTemplates} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
                        <i className="ri-magic-line"></i>{t('newProject.loadStandardTemplate')}
                      </button>
                      <button onClick={() => addPayment()} className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-[#374151] text-sm font-medium cursor-pointer whitespace-nowrap">
                        <i className="ri-add-line"></i>{t('newProject.addManually')}
                      </button>
                    </div>
                  </div>
                )}

                {payments.length > 0 && (
                  <div className="space-y-2.5">
                    {payments.map((payment, idx) => {
                      const isEditing = editingPaymentId === payment.id;
                      return (
                        <div key={payment.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                          {isEditing ? (
                            <div className="p-4 space-y-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-bold text-[#E8621A]">{t('newProject.payment')} {idx + 1}</span>
                                <button onClick={() => setEditingPaymentId(null)} className="text-[#9CA3AF] text-xs cursor-pointer whitespace-nowrap">{t('newProject.done')}</button>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.titleRequired')} <span className="text-red-500">*</span></label>
                                <input type="text" value={payment.title} onChange={(e) => updatePayment(payment.id, 'title', e.target.value)} placeholder="e.g. Deposit" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors" />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.descriptionOptional')} <span className="text-[#9CA3AF] font-normal">{t('newProject.optional')}</span></label>
                                <input type="text" value={payment.description} onChange={(e) => updatePayment(payment.id, 'description', e.target.value)} placeholder="e.g. Progress payment upon completion of framing" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors" />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.amountNZD')} <span className="text-red-500">*</span></label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm">$</span>
                                    <input type="number" value={payment.amount} onChange={(e) => updatePayment(payment.id, 'amount', e.target.value)} placeholder="0" min="0" step="100" className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors" />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.dueDate')}</label>
                                  <input type="date" value={payment.due_date} onChange={(e) => updatePayment(payment.id, 'due_date', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors" />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-[#374151] mb-1">{t('newProject.reference')} <span className="text-[#9CA3AF] font-normal">{t('newProject.optional')}</span></label>
                                <input type="text" value={payment.payment_reference} onChange={(e) => updatePayment(payment.id, 'payment_reference', e.target.value)} placeholder="e.g. INV-001" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors" />
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 p-4">
                              <div className="w-7 h-7 rounded-full bg-[#FEF0E7] flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#E8621A]">{idx + 1}</div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[#1A1A1A] font-semibold text-sm truncate">{payment.title || <span className="text-[#9CA3AF]">{t('newProject.untitledPayment')}</span>}</p>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  {payment.amount && <span className="text-[#059669] text-xs font-bold">${parseFloat(payment.amount).toLocaleString()}</span>}
                                  {payment.due_date && <span className="text-[#9CA3AF] text-xs">{formatDate(payment.due_date)}</span>}
                                  {!payment.amount && <span className="text-[#9CA3AF] text-xs">{t('newProject.noAmountSet')}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => setEditingPaymentId(payment.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                                  <i className="ri-edit-line text-[#9CA3AF] text-sm"></i>
                                </button>
                                <button onClick={() => removePayment(payment.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#FEF2F2] cursor-pointer">
                                  <i className="ri-delete-bin-line text-[#9CA3AF] hover:text-[#EF4444] text-sm transition-colors"></i>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button onClick={() => addPayment()} className="w-full py-3 rounded-2xl border-2 border-dashed border-gray-200 text-[#9CA3AF] text-sm font-medium cursor-pointer hover:border-[#E8621A] hover:text-[#E8621A] transition-colors flex items-center justify-center gap-2 whitespace-normal sm:whitespace-nowrap">
                      <i className="ri-add-line text-base"></i>{t('newProject.addPaymentItem')}
                    </button>
                  </div>
                )}

                {payments.filter((p) => p.amount).length > 0 && (
                  <div className="bg-[#F0FDF4] rounded-xl p-3 flex items-center justify-between">
                    <span className="text-[#059669] text-sm font-semibold">{t('newProject.totalScheduled')}</span>
                    <span className="text-[#059669] font-bold text-base">
                      ${payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0).toLocaleString()} NZD
                    </span>
                  </div>
                )}
              </>
            )}

            {!includePayments && (
              <div className="bg-[#F9FAFB] rounded-xl p-4 flex items-start gap-2">
                <i className="ri-information-line text-[#9CA3AF] text-sm mt-0.5"></i>
                <p className="text-[#6B7280] text-xs leading-relaxed">{t('newProject.setUpPaymentLater')}</p>
              </div>
            )}
          </div>
        )}

        {/* ── Step 7: Review ── */}
        {step === 7 && (
          <div className="space-y-4">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{t('newProject.reviewCreate')}</h2>
              <p className="text-sm text-[#9CA3AF] mt-0.5">{t('newProject.confirmDetails')}</p>
            </div>

            {/* Success state after creation */}
            {createdProjectId && (
              <div className="bg-[#F0FDF4] rounded-2xl p-4 border border-[#86EFAC] space-y-3">
                <div className="flex items-center gap-2">
                  <i className="ri-check-double-line text-[#059669] text-lg"></i>
                  <span className="text-[#059669] font-bold text-sm">{t('newProject.projectCreatedSuccess')}</span>
                </div>

                {initPhase !== 'idle' && initPhase !== 'done' && (
                  <div className="bg-[#EFF6FF] rounded-xl p-3 flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-[#0EA5E9] text-sm"></i>
                    <span className="text-[#0369A1] text-xs font-medium">{initMessage || t('newProject.settingUpSchedule')}</span>
                  </div>
                )}

                {/* Schedule status */}
                <div className="flex items-center gap-2">
                  {scheduleCreated ? (
                    <><i className="ri-calendar-check-line text-[#059669] text-sm"></i><span className="text-[#059669] text-xs font-semibold">{t('newProject.scheduleCreatedOk')}</span></>
                  ) : form.schedule_source !== 'later' && form.create_default_schedule ? (
                    <div className="flex-1 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-error-warning-line text-[#F59E0B] text-sm"></i>
                        <span className="text-[#F59E0B] text-xs font-semibold">{t('newProject.scheduleNotCreated')}</span>
                      </div>
                      {initPhase === 'done' && (
                        <button
                          onClick={handleRetrySchedule}
                          disabled={retryingSchedule}
                          className="text-xs font-semibold text-[#E8621A] hover:underline disabled:opacity-50"
                        >
                          {retryingSchedule ? t('newProject.retrying') : t('newProject.retryScheduleSetup')}
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Checklist status */}
                <div className="flex items-center gap-2">
                  {checklistCreated ? (
                    <><i className="ri-list-check-2 text-[#059669] text-sm"></i><span className="text-[#059669] text-xs font-semibold">{t('newProject.checklistCreatedOk')}</span></>
                  ) : form.create_default_checklist ? (
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <i className="ri-error-warning-line text-[#EF4444] text-sm"></i>
                          <span className="text-[#EF4444] text-xs font-semibold">{t('newProject.checklistNotCreated')}</span>
                        </div>
                        {initPhase === 'done' && (
                          <button
                            onClick={handleRetryChecklist}
                            disabled={retryingChecklist}
                            className="text-xs font-semibold text-[#E8621A] hover:underline disabled:opacity-50"
                          >
                            {retryingChecklist ? t('newProject.retrying') : t('newProject.retryChecklistSetup')}
                          </button>
                        )}
                      </div>
                      <p className="text-[#9CA3AF] text-xs mb-2">{t('newProject.checklistNotCreatedDesc')}</p>
                    </div>
                  ) : null}
                </div>

                {scheduleSetupWarning && (
                  <div className="bg-[#FEF0E7] rounded-xl p-3 flex items-start gap-2">
                    <i className="ri-error-warning-line text-[#F59E0B] text-sm mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#92400E] text-xs">{scheduleSetupWarning}</p>
                  </div>
                )}
              </div>
            )}

            {/* Cover preview */}
            <div className="relative h-32 rounded-2xl overflow-hidden">
              <img src={effectiveCover} alt="Cover" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-2 left-3">
                <div className="text-white font-bold text-sm">{form.project_name}</div>
                <div className="text-white/60 text-xs">{form.site_address || t('newProject.noAddress')}</div>
              </div>
              {form.cover_is_custom ? (
                <div className="absolute top-2 right-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#059669]/80 text-white">{t('newProject.customPhoto')}</span>
                </div>
              ) : (
                <div className="absolute top-2 right-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/40 text-white">{t('newProject.defaultCover')}</span>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-2.5">
              <h3 className="font-bold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2">
                <i className="ri-file-text-line text-[#E8621A]"></i>{t('newProject.projectDetailsSection')}
              </h3>
              {[
                { label: t('newProject.projectNameLabel'), value: form.project_name },
                { label: t('newProject.siteAddressLabel'), value: form.site_address || '—' },
                { label: t('newProject.clientLabel'), value: form.client_name || '—' },
                { label: t('newProject.typeLabel'), value: form.project_type },
                { label: t('newProject.startDateLabel'), value: form.start_date || t('newProject.notSet') },
                { label: t('newProject.estCompletionLabel'), value: form.target_completion_date || t('newProject.notSet') },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4 py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-[#9CA3AF] text-sm flex-shrink-0">{row.label}</span>
                  <span className="text-[#374151] text-sm font-medium text-right">{row.value}</span>
                </div>
              ))}
            </div>

            {/* Schedule setup summary in review */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-bold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2">
                <i className="ri-calendar-todo-line text-[#E8621A]"></i>{t('newProject.scheduleSetupSection')}
              </h3>
              {form.project_type === 'Renovation' ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <i className="ri-check-line text-[#059669] text-xs mt-0.5"></i>
                    <span className="text-[#374151] text-xs leading-relaxed">{t('newProject.lightRenovationSchedulePreview')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-list-check-2 text-[#9CA3AF] text-xs mt-0.5"></i>
                    <span className="text-[#9CA3AF] text-xs leading-relaxed">{t('newProject.stagesAddedLater')}</span>
                  </div>
                </div>
              ) : isBlankCustomProjectType(form.project_type) ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-[#E8621A] text-xs mt-0.5"></i>
                    <span className="text-[#374151] text-xs leading-relaxed">{t('newProject.manualSchedulePreview')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-list-check-2 text-[#9CA3AF] text-xs mt-0.5"></i>
                    <span className="text-[#9CA3AF] text-xs leading-relaxed">{t('newProject.stagesAddedLater')}</span>
                  </div>
                </div>
              ) : form.schedule_source === 'template' && form.create_default_schedule ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.createDefaultFromTemplate')}</span></div>
                  {form.create_default_checklist && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.createDefaultChecklistLabel')}</span></div>}
                  {form.include_dependency_links && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.includeDependencyLinksLabel')}</span></div>}
                  {form.generate_initial_timeline && form.schedule_start_date && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.generateTimelineFrom')} {form.schedule_start_date}</span></div>}
                </div>
              ) : form.schedule_source === 'existing' && form.copy_from_project_id ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.copyScheduleFromExisting')}</span></div>
                  {form.copy_schedule_items && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.copyScheduleItemsLabel')}</span></div>}
                  {form.copy_stages && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.copyStagesLabel')}</span></div>}
                  {form.copy_dependency_links && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.copyDependencyLinksLabel')}</span></div>}
                  {form.copy_checklist_items && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.copyChecklistItemsLabel')}</span></div>}
                  {form.copy_checklist_mappings && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.copyChecklistMappingsLabel')}</span></div>}
                  {form.recalculate_dates && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.recalculateDatesLabel')}</span></div>}
                  {!form.recalculate_dates && <div className="flex items-center gap-2"><i className="ri-check-line text-[#059669] text-xs"></i><span className="text-[#374151] text-xs">{t('newProject.copyAsUndated')}</span></div>}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <i className="ri-close-circle-line text-[#9CA3AF] text-xs"></i>
                  <span className="text-[#9CA3AF] text-xs">{t('newProject.noDefaultSchedule')}</span>
                </div>
              )}
            </div>

            {!isLightweightProjectType(form.project_type) && (
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-bold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2">
                <i className="ri-list-check-2 text-[#E8621A]"></i>{t('newProject.stagesSection')} ({selectedStages.length})
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {selectedStages.map((s, i) => (
                  <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#FEF0E7] text-[#E8621A] font-medium">{i + 1}. {s}</span>
                ))}
              </div>
            </div>
            )}

            {(form.subcontractor_email || form.client_email) && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <h3 className="font-bold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2">
                  <i className="ri-team-line text-[#E8621A]"></i>{t('newProject.teamSection')}
                </h3>
                {form.subcontractor_email && (
                  <div className="flex items-center gap-2 py-1.5 border-b border-gray-50">
                    <i className="ri-tools-line text-[#059669] text-xs"></i>
                    <span className="text-[#374151] text-sm">{form.subcontractor_email}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669] font-medium">{t('newProject.subcontractor')}</span>
                  </div>
                )}
                {form.client_email && (
                  <div className="flex items-center gap-2 py-1.5">
                    <i className="ri-user-line text-[#E8621A] text-xs"></i>
                    <span className="text-[#374151] text-sm">{form.client_email}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A] font-medium">{t('newProject.client')}</span>
                  </div>
                )}
              </div>
            )}

            {includePayments && payments.filter((p) => p.title && p.amount).length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <h3 className="font-bold text-[#1A1A1A] text-sm mb-3 flex items-center gap-2">
                  <i className="ri-bank-card-line text-[#E8621A]"></i>
                  {t('newProject.paymentScheduleSection')} ({payments.filter((p) => p.title && p.amount).length} {t('newProject.paymentItems')})
                </h3>
                <div className="space-y-2">
                  {payments.filter((p) => p.title && p.amount).map((p, i) => (
                    <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-[#374151]">{i + 1}. {p.title}</span>
                      <span className="text-[#059669] text-sm font-bold">${parseFloat(p.amount).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[#9CA3AF] text-xs font-semibold">{t('newProject.total')}</span>
                    <span className="text-[#059669] font-bold text-sm">
                      ${payments.filter((p) => p.amount).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0).toLocaleString()} NZD
                    </span>
                  </div>
                </div>
              </div>
            )}

            {!includePayments && (
              <div className="bg-[#F9FAFB] rounded-xl p-3 flex items-center gap-2">
                <i className="ri-bank-card-line text-[#9CA3AF] text-sm"></i>
                <span className="text-[#9CA3AF] text-xs">{t('newProject.noPaymentScheduleNote')}</span>
              </div>
            )}

            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-red-600 text-sm">{submitError}</p>
              </div>
            )}

            <div className="bg-[#FEF0E7] rounded-2xl p-4">
              <p className="text-[#E8621A] text-sm font-semibold">{t('newProject.readyToCreate')}</p>
              <p className="text-[#B84C10] text-xs mt-1">{t('newProject.readyToCreateDesc')}</p>
            </div>
          </div>
        )}

        {/* ── Navigation ── */}
        <div className="flex gap-3 mt-8">
          {step > 0 && !createdProjectId && (
            <button onClick={goToPrevStep} className="flex-1 py-3.5 rounded-xl border border-gray-200 text-[#374151] font-semibold text-sm cursor-pointer whitespace-normal sm:whitespace-nowrap hover:bg-gray-50 transition-colors">
              {t('newProject.back')}
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button onClick={goToNextStep} disabled={!canProceed()} className="flex-1 py-3.5 rounded-xl bg-[#E8621A] text-white font-semibold text-sm cursor-pointer whitespace-normal sm:whitespace-nowrap disabled:opacity-50 hover:bg-[#B84C10] transition-colors">
              {t('newProject.continue')}
            </button>
          ) : createdProjectId ? (
            <button
              onClick={() => {
                const targetTab = scheduleCreated ? '?tab=Schedule' : checklistCreated ? '?tab=Checklist' : '';
                navigate(`/management/projects/${createdProjectId}${targetTab}`);
              }}
              className="flex-1 py-3.5 rounded-xl bg-[#059669] text-white font-semibold text-sm cursor-pointer whitespace-normal sm:whitespace-nowrap hover:bg-[#047857] transition-colors"
            >
              <span className="flex items-center justify-center gap-2">
                <i className="ri-eye-line"></i>{t('newProject.viewProject')}
              </span>
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-3.5 rounded-xl bg-[#E8621A] text-white font-semibold text-sm cursor-pointer whitespace-normal sm:whitespace-nowrap disabled:opacity-60 hover:bg-[#B84C10] transition-colors">
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  {createdProjectId
                    ? (initMessage || t('newProject.settingUpSchedule'))
                    : t('newProject.creatingProject')}
                </span>
              ) : t('newProject.createProject')}
            </button>
          )}
        </div>
      </div>
    </MgmtLayout>
  );
}